import { useDrugProgram } from '../contexts/DrugProgramProvider';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    type UnclaimedPrescriberOrderInfoFragment,
} from '../graphql/generated/graphql';
import type { EnhancedFillFlowFlag } from '../types/non-boolean-flags';
import { getDrugConfigByMedication } from '../util/config/get-drug-config-by-medication';
import { getNdcDefinition } from '../util/config/get-ndc-definition';

import { useFlags } from './use-flags';
import { useAuthorizedMedication } from './use-order-medication';

export const useHasEnhancedFlow = (
    order:
        | DigitallyReceivedPrescriberOrderInfoFragment
        | UnclaimedPrescriberOrderInfoFragment
        | undefined
) => {
    const { enableEnhancedFillFlow } = useFlags();
    const drugProgram = useDrugProgram();

    const medication = useAuthorizedMedication(order);
    const drugConfig = getDrugConfigByMedication(drugProgram, medication);
    const ndcDefinition = getNdcDefinition(drugProgram, medication);

    const enhancedFlowConfig = enableEnhancedFillFlow as EnhancedFillFlowFlag;

    if (!order || !drugConfig || !ndcDefinition) {
        return false;
    }

    // Unclaimed scripts don't use this flow until we determine how caregiver fits into it
    if (order.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return false;
    }

    if (
        order.status !== PrescriberOrderStatus.New &&
        order.status !== PrescriberOrderStatus.OnHold &&
        order.status !== PrescriberOrderStatus.Pending &&
        order.status !== PrescriberOrderStatus.SentToPharmacy
    ) {
        return false;
    }

    return (
        enhancedFlowConfig.drugKeys.includes(drugConfig.drugKey) ||
        enhancedFlowConfig.ndcs.includes(ndcDefinition.ndc)
    );
};
